/*
 * Cliente del incremento 2/6 · Cuentas y Tarjetas.
 *
 * Usa la fábrica del núcleo (crearCliente) para NO reimplementar la cookie de
 * sesión, la envoltura común de respuesta ni el aviso de fin de sesión al shell.
 *
 * Este módulo es la FUENTE ÚNICA de toda lista de cuentas del sistema: ningún otro
 * módulo consulta la tabla por su cuenta (RF-043).
 */

import { crearCliente } from '../../../nucleo/api/cliente.js';

const api = crearCliente('/api/cuentas');

/** Los tres propósitos de ConsultarCuentas (D-25). No son duplicación (RF-043). */
export const PROPOSITOS = {
  ver: 'VER',
  capturarOrigen: 'CAPTURAR_ORIGEN',
  capturarDestino: 'CAPTURAR_DESTINO',
};

export const CLASES = { todas: 'TODAS', credito: 'CREDITO', noCredito: 'NO_CREDITO' };

function consulta(parametros) {
  const partes = Object.entries(parametros)
    .filter(([, valor]) => valor !== undefined && valor !== null && valor !== '')
    .map(([clave, valor]) => `${clave}=${encodeURIComponent(valor)}`);

  return partes.length > 0 ? `?${partes.join('&')}` : '';
}

/**
 * ConsultarCuentas. La respuesta viene YA ORDENADA por el servidor y el cliente
 * NO reordena (D-29, RN-071). Los totales vienen calculados siempre sobre las
 * cuentas activas, con el control de desactivadas encendido o apagado (RN-072d).
 */
export function consultarCuentas({
  proposito = PROPOSITOS.ver,
  soloActivas = true,
  clase = CLASES.todas,
  idMoneda,
  idUsuarioPropietario,
  incluirSaldos = true,
} = {}) {
  return api.obtener(`/${consulta({ proposito, soloActivas, clase, idMoneda, idUsuarioPropietario, incluirSaldos })}`);
}

export function consultarTiposDeCuenta(soloActivos = true) {
  return api.obtener(`/tipos${consulta({ soloActivos })}`);
}

export function obtenerCuenta(idCuenta) {
  return api.obtener(`/${idCuenta}`);
}

export function consultarUsoDeCuenta(idCuenta) {
  return api.obtener(`/${idCuenta}/uso`);
}

/**
 * ConsultarCorreccionesDeSaldoInicial (RF-176, H-17). De la más reciente a la más
 * antigua, ORDENADO POR EL SERVIDOR: el cliente no reordena.
 *
 * Es de SOLO LECTURA y no tiene hermana de escritura ni de borrado (RNF-101): el
 * hecho lo graba la bitácora del núcleo y aquí solo se lee.
 *
 * NO EXIGE PROPIEDAD: se consulta también sobre una cuenta ajena (RF-172, RN-027).
 * Una cuenta que nunca se corrigió responde con LISTA VACÍA, nunca 404.
 */
export function consultarCorreccionesDeSaldoInicial(idCuenta) {
  return api.obtener(`/${idCuenta}/correcciones-de-saldo-inicial`);
}

/** La fecha de referencia es la FECHA DE NEGOCIO del dispositivo (D-06, RN-016). */
export function consultarFechasDeTarjeta(idCuenta, fechaReferencia) {
  return api.obtener(`/${idCuenta}/fechas${consulta({ fechaReferencia })}`);
}

/**
 * ConsultarFechasDeTarjeta en MODO SIMULACIÓN (D-31, RN-056d): las fechas y los
 * días de gracia de un par de días que la persona acaba de teclear, ANTES de que
 * la cuenta exista (RF-037).
 *
 * Es la MISMA implementación de fin de mes del servidor. **El cliente no calcula
 * fechas ni días de gracia**: aproximar con un mes de 30 días está prohibido y se
 * equivoca hasta en tres días (RN-056b).
 */
export function simularFechasDeTarjeta({ diaCorte, diaLimitePago, fechaReferencia }) {
  return api.obtener(`/fechas${consulta({ diaCorte, diaLimitePago, fechaReferencia })}`);
}

export function validarCuenta(idCuenta, { rol, monedaEsperada, claseEsperada } = {}) {
  return api.obtener(`/${idCuenta}/validacion${consulta({ rol, monedaEsperada, claseEsperada })}`);
}

export function crearCuenta(peticion) {
  return api.publicar('/', peticion);
}

export function actualizarCuenta(idCuenta, peticion) {
  return api.actualizar(`/${idCuenta}`, peticion);
}

/**
 * RenovarTarjeta. Es la MISMA cuenta: conserva identificador, saldo e historial
 * (D-26, RN-070a). No existe en efectivo (D-28).
 */
export function renovarTarjeta(idCuenta, peticion) {
  return api.actualizar(`/${idCuenta}/renovacion`, peticion);
}

/** Desactivar exige saldo en cero y confirmación; reactivar es inmediato (D-22, RN-042). */
export function cambiarEstadoDeCuenta(idCuenta, { activo, confirmado = false, version }) {
  return api.actualizar(`/${idCuenta}/estado`, { activo, confirmado, version });
}
